import React, { Suspense, useEffect, useRef } from 'react';
import { Box, Flex, FormControl } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { PageNoteDialog } from '@shared/components/PageNoteDialog';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { Skeleton } from '@shared/components/Skeleton';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { useStayAreasNoteSave } from '../../hooks/useStayAreasNoteSave';
import { useStayAreas } from '@shared/utils/stayAreas/useStayAreas';
import { useDefaultNeighborhoodsNote } from '@shared/utils/stayAreas/useDefaultNeighborhoodsNote';
import { useStayAreasAutoPopulation } from '@shared/utils/stayAreas/useStayAreasAutoPopulation';
import { useEventInfo } from '@shared/utils/eventInfo/useEventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useUserConsent } from '@shared/utils/userConsent';
import { useEffectOnce } from '@shared/utils/hooks/useEffectOnce';
import { usePreviousValue } from '@shared/utils/hooks/usePreviousValue';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { getValidCoordinates } from '@shared/core/places/location';
import { addError } from '@shared/utils/logger';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useRemoveStayArea } from '../../hooks/useRemoveStayArea';
import { useStayAreasTooltip } from '../../hooks/useStayAreasTooltip';
import { useRegisterDialogIds } from '@website-editor/context/WebsiteEditorDialogContext';
import { ACCOMMODATIONS_ACTIONS } from '../../Accommodations.constants';
import { ACCOMMODATIONS_DIALOGS } from '../../Accommodations.dialogs';
import { AdminStayAreasTelemetryProvider, useAdminStayAreasTelemetry } from '../StayAreas/StayAreas.telemetry';
import { StayAreasStatus } from '../StayAreasStatus';
import { AutoPopulatedStayAreasTooltip } from '../AutoPopulatedStayAreasTooltip/AutoPopulatedStayAreas.Tooltip';
import { StayAreaRemoveDialog } from '../StayAreaRemoveDialog/StayAreaRemoveDialog';
import { HotelTileSkeletonList } from '@shared/components/HotelTile/HotelTile.skeleton';
import TabSectionHeader from '../TabSectionHeader/TabSectionHeader';
import { StayAreasList } from './StayAreasList';
import { StayAreasEmptyState } from '../StayAreas/EmptyState';
import type { AutoPopulationPollingStatus } from '@shared/utils/autoPopulation/useAutoPopulation.types';

const STAY_AREAS_DIALOG_IDS: readonly string[] = [ACCOMMODATIONS_DIALOGS.STAY_AREAS_TOOLTIP];

const StayAreasTabContent: React.FC = () => {
  useRegisterDialogIds(STAY_AREAS_DIALOG_IDS);
  const isAutoPopulationStartedRef = useRef(false);
  const isExistingEventAutoPopulationRef = useRef(false);

  const { tq } = useTranslation('stayAreasAdmin');
  const { loading, stayAreas, refetch } = useStayAreas();
  const { eventInfo } = useEventInfo();

  const { travelSettings, isSuperAdmin } = useAccommodationsContext();
  const savedStayAreasNote = travelSettings?.accommodationsNeighborhoodsNote ?? '';
  // Show the default note (the same guest fallback) when nothing is saved, so the admin field mirrors guests.
  const defaultStayAreasNote = useDefaultNeighborhoodsNote();
  const displayedStayAreasNote = savedStayAreasNote || defaultStayAreasNote;
  const canEditNote = Boolean(eventInfo?.eventId);
  const { dialogState: noteDialogState, openDialog: openNoteDialog, closeDialog: closeNoteDialog, handleSave: handleNoteSave, isSaving: isSavingNote } = useStayAreasNoteSave();

  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { venueInfo, loading: loadingVenue } = useEventPlaces();
  const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();

  const { startAutoPopulation, restartPolling, status, results, operationsCount, pollingQueryLoading } = useStayAreasAutoPopulation({
    mode: 'auto',
    context: 'StayAreasStatus',
    onComplete: async () => {
      await refetch();
      updateWebsitePreview();
      isAutoPopulationStartedRef.current = false;
    },
  });

  const hasAutoPopulatedResults = results.length > 0;

  // Venue change detection: when venue changes (e.g. from EventVenue dialog),
  // restart polling so this component picks up the new job status.
  const googlePlaceId = venueInfo?.googlePlaceId;
  const prevGooglePlaceId = usePreviousValue(googlePlaceId);
  useEffect(() => {
    if (prevGooglePlaceId && googlePlaceId && prevGooglePlaceId !== googlePlaceId) {
      isAutoPopulationStartedRef.current = true;
      restartPolling();
    }
  }, [prevGooglePlaceId, googlePlaceId, restartPolling]);

  // Existing-event auto-population: auto-start for eligible events that never had stay areas.
  const venueLocation = getValidCoordinates(venueInfo);
  const neverAutoPopulated = operationsCount === 0;
  const isLoadingEligibility = loadingUserConsent || loadingVenue || loading || pollingQueryLoading;
  const isExistingEventEligible =
    !!eventInfo?.eventId &&
    !isLoadingEligibility &&
    neverAutoPopulated &&
    !['PENDING', 'FAILED'].includes(status) &&
    stayAreas.length === 0 &&
    !getUserConsent('showStayAreasOnGuestSite') &&
    !!venueLocation;

  useEffectOnce(isExistingEventEligible, async () => {
    isExistingEventAutoPopulationRef.current = true;
    try {
      await setUserConsent('showStayAreasOnGuestSite', false);
      await startAutoPopulation({ venue: venueLocation });
    } catch (error) {
      addError(error, { context: 'StayAreasTab.existingEventAutoPopulation' });
      isExistingEventAutoPopulationRef.current = false;
    }
  });

  const { stayAreaToDelete, deletedIds, handleRemove, handleCloseDialog, handleConfirmRemove } = useRemoveStayArea();
  const {
    shouldShowTooltip,
    handleTooltipClose,
    showCta,
    handleCtaClick,
    loading: loadingCta,
  } = useStayAreasTooltip({
    status,
    hasAutoPopulatedResults,
  });

  const visibleStayAreas = stayAreas.filter(sa => !deletedIds.has(sa.id));

  const { navigateToScreen } = useNavigationPaneContext();
  const telemetry = useAdminStayAreasTelemetry();

  const startAutoPopulationFlow = useEventCallback(() => {
    isAutoPopulationStartedRef.current = true;
    const venue = getValidCoordinates(venueInfo);
    startAutoPopulation({ venue });
  });

  // Header CTA logs its own click here; StayAreasEmptyState logs the empty-state
  // click itself, so it gets the raw flow to avoid double-counting.
  const handleHeaderSuggestClick = useEventCallback(() => {
    telemetry.showSuggestedStayAreasClicked({ source: 'header' });
    startAutoPopulationFlow();
  });

  const handleAddNeighborhood = useEventCallback(() => {
    telemetry.addStayAreaStarted();
    navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_STAY_AREA_ADD);
  });

  // "Add AI suggestions" inline escape hatch from PRD §5.7 / FE plan §3.8.
  // Only meaningful when the host already has entries AND has never auto-populated;
  // BE PR #1382 preserves source=manual rows when the AI runs, so this is safe.
  const showAddAiSuggestionsCta = visibleStayAreas.length > 0 && neverAutoPopulated && status !== 'PENDING';

  const isAwaitingResults = (isAutoPopulationStartedRef.current || isExistingEventAutoPopulationRef.current) && visibleStayAreas.length === 0 && status !== 'FAILED';
  const effectiveStatus: AutoPopulationPollingStatus = isAwaitingResults ? 'PENDING' : pollingQueryLoading ? 'IDLE' : status;

  const showEmptyState = !loading && visibleStayAreas.length === 0 && effectiveStatus !== 'PENDING' && !isExistingEventEligible;

  return (
    <Flex
      flexDirection="column"
      gap={6}
    >
      {/* Note to Guests — section intro note, shown above the Neighborhoods header (mirrors Hotels tab) */}
      {isSavingNote ? (
        <Flex
          width="100%"
          paddingX={{ _: 6, sm2: 8 }}
        >
          <Skeleton
            width="100%"
            height={pxToRem(92.19)}
            borderRadius={pxToRem(8)}
          />
        </Flex>
      ) : (
        <FormControl
          typographyVariant="button2"
          label={tq`Note to Guests`}
          paddingX={{ _: 6, sm2: 8 }}
        >
          <Box
            as="button"
            width="100%"
            type="button"
            textAlign="left"
            border="1px solid"
            borderColor="mono5"
            paddingX={4}
            paddingY={3}
            typographyVariant="body2"
            borderRadius={pxToRem(8)}
            aria-disabled={!canEditNote}
            onClick={() => canEditNote && openNoteDialog()}
            cursor={canEditNote ? 'pointer' : 'not-allowed'}
          >
            <TextEllipsisWrapper lineClamp={2}>{displayedStayAreasNote}</TextEllipsisWrapper>
          </Box>
        </FormControl>
      )}

      {noteDialogState === 'open' && (
        <PageNoteDialog
          title={tq`Neighborhoods Note`}
          initialNote={displayedStayAreasNote}
          onSave={handleNoteSave}
          isOpen={true}
          isLoading={isSavingNote}
          onClose={closeNoteDialog}
          showHelpTooltip={false}
          disableMaxLength={isSuperAdmin}
        />
      )}

      <TabSectionHeader title={tq`Neighborhoods`}>
        {showAddAiSuggestionsCta && (
          <TabSectionHeader.AiSuggest
            showIcon={false}
            onClick={handleHeaderSuggestClick}
          >
            {tq`Suggest`}
          </TabSectionHeader.AiSuggest>
        )}
        <TabSectionHeader.Action onClick={handleAddNeighborhood}>{tq`Add`}</TabSectionHeader.Action>
      </TabSectionHeader>

      <Flex
        flexDirection="column"
        gap={6}
        paddingX={{ _: 6, sm2: 8 }}
      >
        <StayAreasStatus
          status={effectiveStatus}
          shouldShowTooltip={shouldShowTooltip}
        />

        <AutoPopulatedStayAreasTooltip
          isOpen={shouldShowTooltip}
          onClose={handleTooltipClose}
          showCta={showCta}
          loading={loadingCta}
          onCtaClick={handleCtaClick}
        />

        {showEmptyState && !pollingQueryLoading ? (
          <StayAreasEmptyState onShowSuggestedStayAreas={startAutoPopulationFlow} />
        ) : (
          <StayAreasList
            loading={loading}
            stayAreas={visibleStayAreas}
            onRemove={handleRemove}
          />
        )}
      </Flex>

      <StayAreaRemoveDialog
        isOpen={stayAreaToDelete !== null}
        stayAreaName={stayAreaToDelete?.name}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmRemove}
      />
    </Flex>
  );
};

const StayAreasTab: React.FC = () => {
  return (
    <AdminStayAreasTelemetryProvider>
      <Suspense
        fallback={
          <Flex
            flexDirection="column"
            gap={6}
            paddingBottom={pxToRem(80)}
            paddingX={{ _: 6, sm2: 8 }}
          >
            <HotelTileSkeletonList />
          </Flex>
        }
      >
        <StayAreasTabContent />
      </Suspense>
    </AdminStayAreasTelemetryProvider>
  );
};

export default StayAreasTab;
